import { KeyRound } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { RapidSOSMark } from '@/components/RapidSOSMark/RapidSOSMark';
import { RapidSOSWordmark } from '@/components/RapidSOSMark/RapidSOSWordmark';
import { DEFAULT_MARK_ANIMATION } from '@/components/RapidSOSMark/animations';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/hooks/useAuth';
import { readLastStudioSignIn } from '@/lib/studioIdentity';

function GoogleMark({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 48 48" aria-hidden="true">
      <path
        fill="#FFC107"
        d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.3 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.1 8 3l5.7-5.7C34.2 6.1 29.4 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.2-.1-2.3-.4-3.5z"
      />
      <path
        fill="#FF3D00"
        d="M6.3 14.7l6.6 4.8C14.7 16.1 19 12 24 12c3.1 0 5.8 1.1 8 3l5.7-5.7C34.2 6.1 29.4 4 24 4 16.3 4 9.6 8.3 6.3 14.7z"
      />
      <path
        fill="#4CAF50"
        d="M24 44c5.2 0 10-2 13.6-5.2l-6.3-5.3C29.1 35.1 26.7 36 24 36c-5.3 0-9.7-3.3-11.3-7.9l-6.5 5C9.5 39.6 16.2 44 24 44z"
      />
      <path
        fill="#1976D2"
        d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.3 4.1-4.2 5.5l.1.1 6.3 5.3C39.1 37.3 44 33 44 24c0-1.2-.1-2.3-.4-3.5z"
      />
    </svg>
  );
}

export function SignInPage() {
  const { t } = useTranslation();
  const { loginWithRedirect } = useAuth();
  const lastSignIn = useMemo(() => readLastStudioSignIn(), []);
  const [email, setEmail] = useState(lastSignIn?.email ?? '');
  const [submitting, setSubmitting] = useState(false);

  const signIn = (opts?: { loginHint?: string; connection?: string }) => {
    if (submitting) return;
    setSubmitting(true);
    void loginWithRedirect({
      authorizationParams: {
        ...(opts?.loginHint ? { login_hint: opts.loginHint } : {}),
        ...(opts?.connection ? { connection: opts.connection } : {}),
      },
    }).finally(() => {
      setSubmitting(false);
    });
  };

  return (
    <main className="flex min-h-full w-full items-center justify-center bg-background px-6 py-16">
      <div className="w-full max-w-sm">
        <div className="flex items-center justify-center gap-2 text-foreground">
          <RapidSOSMark
            key={DEFAULT_MARK_ANIMATION}
            className="h-6 w-6"
            animation={DEFAULT_MARK_ANIMATION}
          />
          <RapidSOSWordmark className="h-6" />
        </div>

        <h1 className="mt-8 text-center text-2xl font-semibold tracking-tight text-foreground">
          {lastSignIn ? t('auth.signedOutTitle') : t('auth.signInTitle')}
        </h1>
        <p className="mt-2 text-center text-sm text-muted-foreground">
          {lastSignIn ? t('auth.signedOutSubtitle') : t('auth.signInSubtitle')}
        </p>

        <div className="mt-8 rounded-xl border border-border bg-card p-5">
          {lastSignIn ? (
            <Button
              type="button"
              className="w-full gap-2 bg-primary text-primary-foreground hover:bg-primary/90"
              disabled={submitting}
              onClick={() => signIn({ loginHint: lastSignIn.email })}
            >
              <KeyRound className="h-4 w-4" aria-hidden="true" />
              {t('auth.continueAs', { name: lastSignIn.name })}
            </Button>
          ) : (
            <Button
              type="button"
              className="w-full gap-2 bg-primary text-primary-foreground hover:bg-primary/90"
              disabled={submitting}
              onClick={() => signIn()}
            >
              <KeyRound className="h-4 w-4" aria-hidden="true" />
              {t('auth.continueWithRapidsos')}
            </Button>
          )}

          <Button
            type="button"
            variant="outline"
            className="mt-3 w-full gap-2"
            disabled={submitting}
            onClick={() => signIn({ connection: 'google-oauth2' })}
          >
            <GoogleMark className="h-4 w-4" />
            {t('auth.continueWithGoogle')}
          </Button>

          <div className="my-4 flex items-center gap-3">
            <span className="h-px flex-1 bg-border" aria-hidden="true" />
            <span className="text-xs text-muted-foreground">{t('common.or')}</span>
            <span className="h-px flex-1 bg-border" aria-hidden="true" />
          </div>

          <form
            onSubmit={(event) => {
              event.preventDefault();
              signIn({ loginHint: email.trim() || undefined });
            }}
            className="space-y-3"
          >
            <div>
              <Label htmlFor="studio-email" className="text-xs font-medium text-muted-foreground">
                {t('auth.workEmail')}
              </Label>
              <Input
                id="studio-email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                placeholder={t('auth.emailPlaceholder')}
                className="mt-1.5 h-10"
              />
            </div>
            <Button type="submit" variant="outline" className="w-full" disabled={submitting}>
              {t('auth.continue')}
            </Button>
          </form>
        </div>

        <p className="mt-6 text-center text-xs text-muted-foreground">
          {t('auth.accessManaged')}
        </p>
      </div>
    </main>
  );
}
